import { useUiThemes } from '@/hooks/use-ui-themes';
import { useTerminalAudio } from '@/hooks/use-terminal-audio';
import { useT } from '@/i18n/use-t';
import { useState, type KeyboardEvent } from 'react';
import {
  isAbsoluteStationPath,
  type TerminalParamDescriptor,
  type TerminalParamValue,
} from '@reform-lite/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { majorToMinor, minorToMajor } from './param-value';

/**
 * One input per parameter value type, rendered from the descriptor alone
 * (RL-272, TP-FR-012).
 *
 * This file is the whole reason the module exists: it is what makes "add a
 * key to the registry" sufficient to make that key authorable. There is no
 * per-key branch here and there must never be one — a parameter that needs
 * its own widget needs its own value type, declared in the contract, so the
 * cost is paid once and visibly rather than as a UI ticket per key.
 *
 * `onChange` reports the parsed value and, for the text-backed types, the
 * reason the text could not be parsed. While an error is reported the value
 * is the last one that parsed, so a caller must treat a non-null error as
 * "not saveable" rather than trusting the value.
 *
 * Text-backed fields keep their own draft text and seed it from `value` on
 * mount. A caller that resets the value from outside — cancel, save, a
 * refetch — remounts the field with a new `key` so the text follows.
 */
export type ParamFieldProps = {
  descriptor: TerminalParamDescriptor;
  value: TerminalParamValue;
  onChange: (next: TerminalParamValue, error: string | null) => void;
  disabled?: boolean;
  /** DOM id for the primary input, so a visible label can point at it. */
  id: string;
  /**
   * RL-423: the key declares `whenUnset: 'keepCurrent'` and is set at no
   * scope, so the till leaves the device as it is. `value` is then only the
   * registry default and must not read as the setting in effect: a bool is
   * rendered as an explicit choice that shows "not set" until the operator
   * picks On or Off. `selected` is false once a draft value was picked.
   */
  unset?: { selected: boolean };
};

export function ParamField(props: ParamFieldProps) {
  if (props.descriptor.optionsSource !== undefined) return <CatalogueField {...props} />;
  switch (props.descriptor.type) {
    case 'bool':
      return <BoolField {...props} />;
    case 'enum':
      return <EnumField {...props} />;
    case 'string':
      return <StringField {...props} />;
    case 'timeOfDay':
      return <TimeOfDayField {...props} />;
    case 'int':
    case 'durationSeconds':
      return <IntegerField {...props} />;
    case 'money':
      return <MoneyField {...props} />;
    case 'percent':
      return <PercentField {...props} />;
    case 'stringList':
      return <StringListField {...props} />;
  }
}

function BoolField({ descriptor, value, onChange, disabled, id, unset }: ParamFieldProps) {
  const t = useT();
  if (unset !== undefined) {
    return (
      <Select
        id={id}
        aria-label={descriptor.label}
        className="max-w-sm"
        value={unset.selected ? '' : value === true ? 'on' : 'off'}
        disabled={disabled}
        onChange={(event) => {
          if (event.target.value === '') return;
          onChange(event.target.value === 'on', null);
        }}
      >
        {unset.selected && (
          <option value="">
            {t(
              'parameters.field.notSetKeepsCurrent',
              'Not set — the till keeps its current setting',
            )}
          </option>
        )}
        <option value="on">{t('parameters.field.on', 'On')}</option>
        <option value="off">{t('parameters.field.off', 'Off')}</option>
      </Select>
    );
  }
  return (
    <label className="flex h-10 items-center gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 accent-primary"
        aria-label={descriptor.label}
        checked={value === true}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked, null)}
      />
      {value === true ? t('parameters.field.on', 'On') : t('parameters.field.off', 'Off')}
    </label>
  );
}

function EnumField({ descriptor, value, onChange, disabled, id }: ParamFieldProps) {
  const t = useT();
  return (
    <Select
      id={id}
      aria-label={descriptor.label}
      className="max-w-sm"
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value, null)}
    >
      {(descriptor.enumValues ?? []).map((option) => (
        <option key={option} value={option}>
          {/* VL-365: an option's words when the catalogue has them
              (`params.<key>.option.<value>`); the stored value otherwise,
              exactly as before. */}
          {t(`params.${descriptor.key}.option.${option}`, option)}
        </option>
      ))}
    </Select>
  );
}

function StringField({ descriptor, value, onChange, disabled, id }: ParamFieldProps) {
  return (
    <Input
      id={id}
      aria-label={descriptor.label}
      className="max-w-sm"
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value, null)}
    />
  );
}

function TimeOfDayField({ descriptor, value, onChange, disabled, id }: ParamFieldProps) {
  return (
    <Input
      id={id}
      aria-label={descriptor.label}
      className="max-w-[8rem] font-mono"
      placeholder="HH:mm"
      inputMode="numeric"
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value, null)}
    />
  );
}

function IntegerField({ descriptor, value, onChange, disabled, id }: ParamFieldProps) {
  const t = useT();
  const [text, setText] = useState(typeof value === 'number' ? String(value) : '');
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        aria-label={descriptor.label}
        className="max-w-[10rem] font-mono"
        inputMode="numeric"
        value={text}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          if (/^-?\d+$/.test(next.trim())) {
            onChange(Number.parseInt(next.trim(), 10), null);
          } else {
            onChange(value, t('parameters.field.wholeNumber', 'Enter a whole number.'));
          }
        }}
      />
      {descriptor.type === 'durationSeconds' && (
        <span className="text-xs text-muted-foreground">
          {t('parameters.field.seconds', 'seconds')}
        </span>
      )}
    </div>
  );
}

function MoneyField({ descriptor, value, onChange, disabled, id }: ParamFieldProps) {
  const [text, setText] = useState(
    typeof value === 'string' && /^-?\d+$/.test(value) ? minorToMajor(value) : '',
  );
  return (
    <Input
      id={id}
      aria-label={descriptor.label}
      className="max-w-[12rem] font-mono"
      placeholder="0.00"
      inputMode="decimal"
      value={text}
      disabled={disabled}
      onChange={(event) => {
        const next = event.target.value;
        setText(next);
        const parsed = majorToMinor(next);
        if ('error' in parsed) onChange(value, parsed.error);
        else onChange(parsed.value, null);
      }}
    />
  );
}

function PercentField({ descriptor, value, onChange, disabled, id }: ParamFieldProps) {
  return (
    <div className="flex items-center gap-2">
      <Input
        id={id}
        aria-label={descriptor.label}
        className="max-w-[8rem] font-mono"
        inputMode="decimal"
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value.trim().replace(',', '.'), null)}
      />
      <span className="text-xs text-muted-foreground">%</span>
    </div>
  );
}

/**
 * Add/remove rows (TP-FR-013). Equal in capability to the hand-built discount
 * reason editor it replaces: entries are trimmed, blanks and duplicates are
 * refused before they reach the list, Enter adds.
 */
function StringListField({ descriptor, value, onChange, disabled, id }: ParamFieldProps) {
  const t = useT();
  const entries = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const entry = draft.trim();
    if (entry.length === 0) {
      setError(t('parameters.field.enterValue', 'Enter a value before adding it.'));
      return;
    }
    if (entries.includes(entry)) {
      setError(t('parameters.field.duplicate', 'That entry is already in the list.'));
      return;
    }
    onChange([...entries, entry], null);
    setDraft('');
    setError(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      add();
    }
  };

  return (
    <div className="max-w-xl space-y-2">
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('parameters.field.noEntries', 'No entries.')}
        </p>
      ) : (
        <ol className="space-y-1" aria-label={descriptor.label}>
          {entries.map((entry, index) => (
            <li
              key={entry}
              className="flex items-center justify-between rounded border bg-background px-3 py-1.5 text-sm"
            >
              <span>{entry}</span>
              {!disabled && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`${t('parameters.field.remove', 'Remove')} ${entry}`}
                  onClick={() =>
                    onChange(
                      entries.filter((_, itemIndex) => itemIndex !== index),
                      null,
                    )
                  }
                >
                  {t('parameters.field.remove', 'Remove')}
                </Button>
              )}
            </li>
          ))}
        </ol>
      )}
      {!disabled && (
        <div className="flex gap-2">
          <Input
            id={id}
            aria-label={`${t('parameters.field.addTo', 'Add to')} ${descriptor.label}`}
            value={draft}
            maxLength={512}
            placeholder={t('parameters.field.newEntry', 'New entry')}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
            onKeyDown={onKeyDown}
          />
          <Button type="button" variant="outline" onClick={add}>
            {t('parameters.field.add', 'Add')}
          </Button>
        </div>
      )}
      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

/** Catalogue metadata selects this widget; parameter keys remain generic. */
/**
 * The options behind `descriptor.optionsSource`, whichever catalogue it names.
 *
 * Both hooks are called unconditionally — hooks must be — and the one that is
 * not wanted is disabled, so a themes field issues no audio request and vice
 * versa.
 */
function useCatalogueOptions(source: 'uiThemes' | 'terminalAudio') {
  const themes = useUiThemes(source === 'uiThemes');
  const audio = useTerminalAudio(source === 'terminalAudio');
  if (source === 'terminalAudio') {
    return {
      isLoading: audio.isLoading,
      error: audio.error,
      // The sha-256 is the stored value; the filename is only what an
      // operator recognises it by.
      options: (audio.data ?? []).map((asset) => ({
        id: asset.contentHash,
        name: asset.originalFilename,
      })),
    };
  }
  return {
    isLoading: themes.isLoading,
    error: themes.error,
    options: (themes.data ?? []).map((theme) => ({ id: theme.id, name: theme.name })),
  };
}

/** Sentinel for "not in the catalogue" — never a stored value. */
const CUSTOM = '__custom__';

type CatalogueOption = { id: string; name: string };

/**
 * A single-value catalogue field, optionally with an escape hatch.
 *
 * `optionsCustomValue` exists because a catalogue otherwise lands as a cliff:
 * the moment the picker appears, every value the previous mechanism used
 * becomes unauthorable, even when the thing consuming it has not changed yet.
 * For terminal sounds the station still resolves a filesystem path (RL-418),
 * so the list and a path have to coexist until it does not.
 *
 * The two controls never both hold the value. The select chooses which mode
 * the field is in; the text input only appears in custom mode and is the sole
 * author of the value there.
 */
function CatalogueStringField({
  descriptor,
  value,
  onChange,
  disabled,
  id,
  source,
  options,
}: ParamFieldProps & { source: 'uiThemes' | 'terminalAudio'; options: CatalogueOption[] }) {
  const t = useT();
  const current = typeof value === 'string' ? value : '';
  const allowCustom = descriptor.optionsCustomValue === 'absolutePath';
  const inCatalogue = current !== '' && options.some((option) => option.id === current);
  const isCustomValue = allowCustom && isAbsoluteStationPath(current);
  // The explicit bit keeps path mode open while the operator types an
  // incomplete path. A stored value enters custom mode only when it matches
  // the declared custom syntax: a deleted hash remains an unavailable
  // catalogue value rather than being mislabeled as a filesystem path.
  const [editingCustom, setEditingCustom] = useState(isCustomValue);
  const custom = editingCustom || isCustomValue;

  return (
    <div className="space-y-2">
      <Select
        id={id}
        aria-label={descriptor.label}
        value={custom ? CUSTOM : current}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value;
          if (next === CUSTOM) {
            setEditingCustom(true);
            // Keep whatever is stored if it is already custom; otherwise start
            // empty rather than carrying a hash into a path field.
            onChange(isCustomValue ? current : '', null);
            return;
          }
          setEditingCustom(false);
          onChange(next, null);
        }}
      >
        {/*
          The empty choice is how an operator gets back to the built-in
          behaviour — for a sound, the station's own tone. Without it the only
          way to undo a selection is to clear the whole parameter, which is a
          different act with a different scope.
        */}
        <option value="">
          {source === 'terminalAudio'
            ? t('terminalAudio.none', 'Built-in tone')
            : t('themes.none', 'None')}
        </option>
        {current !== '' && !inCatalogue && !isCustomValue && (
          <option value={current} disabled>
            {current} — {t('themes.inactive', 'unavailable')}
          </option>
        )}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
        {allowCustom && (
          <option value={CUSTOM}>{t('terminalAudio.customPath', 'A path on the station…')}</option>
        )}
      </Select>

      {custom && (
        <div className="space-y-1">
          <Input
            id={`${id}-custom`}
            aria-label={t('terminalAudio.customPath', 'A path on the station…')}
            className="max-w-sm font-mono"
            placeholder="/srv/sounds/chime.wav"
            value={current}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value.trim(), null)}
          />
          <p className="text-xs text-muted-foreground">
            {t(
              'terminalAudio.customPathHelp',
              'A file already on that station. Uploaded sounds are the durable choice — a path only works where someone has put the file.',
            )}
          </p>
        </div>
      )}
    </div>
  );
}

function CatalogueField({ descriptor, value, onChange, disabled, id }: ParamFieldProps) {
  const source = descriptor.optionsSource ?? 'uiThemes';
  const catalogue = useCatalogueOptions(source);
  const t = useT();
  const entries = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
  const options = catalogue.options;
  if (catalogue.isLoading)
    return (
      <p>
        {source === 'terminalAudio'
          ? t('terminalAudio.loading', 'Loading sounds…')
          : t('themes.loading', 'Loading themes…')}
      </p>
    );
  if (catalogue.error)
    return (
      <p role="alert">
        {source === 'terminalAudio'
          ? t('terminalAudio.unavailable', 'Sound list unavailable. Reload before editing.')
          : t('themes.unavailable', 'Theme catalogue unavailable. Reload before editing.')}
      </p>
    );
  if (descriptor.type === 'string')
    return (
      <CatalogueStringField
        {...{ descriptor, value, onChange, disabled, id }}
        source={source}
        options={options}
      />
    );
  const all = [
    ...options,
    ...entries
      .filter((entry) => !options.some((theme) => theme.id === entry))
      .map((entry) => ({ id: entry, name: `${entry} — ${t('themes.inactive', 'unavailable')}` })),
  ];
  return (
    <fieldset id={id} disabled={disabled} aria-label={descriptor.label} className="space-y-2">
      {all.map((theme) => (
        <label key={theme.id} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={entries.includes(theme.id)}
            onChange={(event) =>
              onChange(
                event.target.checked
                  ? [...entries, theme.id]
                  : entries.filter((entry) => entry !== theme.id),
                null,
              )
            }
          />
          {theme.name}
        </label>
      ))}
    </fieldset>
  );
}
